/**
 * SEO Landing Page - Stillasjobb Oslo
 * Public page for attracting scaffolding workers through organic search
 */
import { useState } from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { LanguageSwitcher } from "@/components/LanguageSwitcher";
import {
  HardHat,
  MapPin,
  CheckCircle2,
  Wrench,
  Building2,
  Clock,
  Phone,
  Mail,
  ChevronRight,
  Star,
  Award,
  Briefcase,
  ArrowRight,
  Upload,
  Users,
  User,
  FileText,
  Send,
  Loader2,
  Play,
  Zap,
  AlertCircle,
  Image as ImageIcon
} from "lucide-react";

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function StillasjobbOslo() {
  const { t, i18n } = useTranslation();
  const lang = i18n.language === "en" ? "en" : "no";
  
  // Form state
  const [formData, setFormData] = useState({
    full_name: "",
    email: "",
    phone: "",
    experience: "",
    certifications: "",
    message: "",
    is_team: false,
    team_size: 2
  });
  const [cvFile, setCvFile] = useState(null);
  const [cvUrl, setCvUrl] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  // Handle CV upload
  const handleCvUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    // Check file size (max 10MB for CV/images)
    if (file.size > 10 * 1024 * 1024) {
      toast.error(lang === "no" ? "Filen er for stor (maks 10MB)" : "File is too large (max 10MB)");
      return;
    }
    
    // Validate file type
    const allowedTypes = ['application/pdf', 'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document', 'image/jpeg', 'image/png', 'image/webp', 'image/jpg'];
    if (!allowedTypes.includes(file.type) && !file.type.startsWith('image/')) {
      toast.error(lang === "no" ? "Ugyldig filtype. Bruk PDF, DOC eller bilder (JPG, PNG)" : "Invalid file type. Use PDF, DOC or images (JPG, PNG)");
      return;
    }
    
    setCvFile(file);
    setUploading(true);
    
    try {
      const formDataUpload = new FormData();
      formDataUpload.append('file', file);
      formDataUpload.append('folder', 'job_applications');
      
      const res = await fetch(`${API_URL}/api/projects-v3/public-upload`, {
        method: 'POST',
        body: formDataUpload
      });
      
      if (res.ok) {
        const data = await res.json();
        setCvUrl(data.secure_url);
        const isImage = file.type.startsWith('image/');
        toast.success(lang === "no" 
          ? (isImage ? "Bilde lastet opp!" : "CV lastet opp!") 
          : (isImage ? "Image uploaded!" : "CV uploaded!"));
      } else {
        toast.error(lang === "no" ? "Kunne ikke laste opp filen" : "Could not upload file");
        setCvFile(null);
      }
    } catch (error) {
      toast.error(lang === "no" ? "Feil ved opplasting" : "Upload error");
      setCvFile(null);
    } finally {
      setUploading(false);
    }
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.full_name || !formData.email || !formData.phone) {
      toast.error(lang === "no" ? "Vennligst fyll ut alle påkrevde felt" : "Please fill in all required fields");
      return;
    }
    
    setSubmitting(true);
    
    try {
      const res = await fetch(`${API_URL}/api/projects-v3/jobs/public-apply`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...formData,
          cv_url: cvUrl
        })
      });
      
      if (res.ok) {
        // Google Ads Conversion Tracking
        if (typeof window.gtag === 'function') {
          window.gtag('event', 'conversion', {
            'send_to': 'AW-18035432072/zLNqCNSS8Y0cEIi1-5dD',
            'value': 1.0,
            'currency': 'NOK'
          });
        }
        setSubmitted(true);
        toast.success(lang === "no" ? "Søknad sendt! Vi kontakter deg snart." : "Application sent! We'll contact you soon.");
      } else {
        toast.error(lang === "no" ? "Noe gikk galt. Prøv igjen." : "Something went wrong. Try again.");
      }
    } catch (error) {
      toast.error("Feil ved sending av søknad");
    } finally {
      setSubmitting(false);
    }
  };

  // FAQ data (bilingual)
  const faqDataNo = [
    { q: "Trenger jeg fagbrev?", a: "Erfaring og sertifisering er en fordel, men ikke et absolutt krav. Vi vurderer alle søkere basert på kompetanse og motivasjon." },
    { q: "Kan lag søke sammen?", a: "Ja, både enkeltpersoner og komplette lag er velkomne. Vi setter pris på etablerte team som allerede jobber godt sammen." },
    { q: "Hvordan søker jeg?", a: "Fyll ut skjemaet på denne siden – det tar kun 2 minutter. Vi tar kontakt med deg så snart som mulig." },
    { q: "Hva slags prosjekter jobber dere med?", a: "Vi arbeider med både LAYHER (lett stillas) og HAKI (tungt stillas) på bygg- og anleggsprosjekter i Oslo og Viken-området." },
    { q: "Er dette fast ansettelse?", a: "Vi tilbyr fleksible arbeidsforhold med mulighet for faste oppdrag basert på prosjekttilgang og din tilgjengelighet." }
  ];
  
  const faqDataEn = [
    { q: "Do I need certification?", a: "Experience and certification is an advantage, but not an absolute requirement. We evaluate all applicants based on competence and motivation." },
    { q: "Can teams apply together?", a: "Yes, both individuals and complete teams are welcome. We appreciate established teams that already work well together." },
    { q: "How do I apply?", a: "Fill out the form on this page – it only takes 2 minutes. We'll contact you as soon as possible." },
    { q: "What kind of projects do you work with?", a: "We work with both LAYHER (light scaffolding) and HAKI (heavy scaffolding) on construction projects in Oslo and Viken area." },
    { q: "Is this permanent employment?", a: "We offer flexible working conditions with the possibility of permanent assignments based on project availability and your availability." }
  ];
  
  const faqData = lang === "en" ? faqDataEn : faqDataNo;

  // FAQ Schema for rich results (always in Norwegian for Google.no)
  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": [
      {
        "@type": "Question",
        "name": "Trenger jeg fagbrev for å jobbe som stillasmontør?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Erfaring og sertifisering er en fordel, men ikke et absolutt krav. Vi vurderer alle søkere basert på kompetanse og motivasjon."
        }
      },
      {
        "@type": "Question",
        "name": "Kan stillaslag søke sammen?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Ja, både enkeltpersoner og komplette lag er velkomne. Vi setter pris på etablerte team som allerede jobber godt sammen."
        }
      },
      {
        "@type": "Question",
        "name": "Hvordan søker jeg jobb hos AllStillas?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Klikk på 'Søk i dag' og fyll ut registreringsskjemaet – det tar kun 2 minutter. Vi tar kontakt med deg så snart som mulig."
        }
      },
      {
        "@type": "Question",
        "name": "Hva slags stillasprosjekter jobber AllStillas med?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Vi arbeider med både LAYHER (lett stillas) og HAKI (tungt stillas) på bygg- og anleggsprosjekter i Oslo og Viken-området."
        }
      },
      {
        "@type": "Question",
        "name": "Er dette fast ansettelse eller oppdragsbasert?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Vi tilbyr fleksible arbeidsforhold med mulighet for faste oppdrag basert på prosjekttilgang og din tilgjengelighet."
        }
      }
    ]
  };
  
  return (
    <>
      {/* SEO Meta Tags - Keep in Norwegian for Google.no */}
      <Helmet>
        <title>Stillasjobb i Oslo | Bli stillasmontør hos AllStillas</title>
        <meta 
          name="description" 
          content="Søk jobb som stillasmontør i Oslo. AllStillas tilbyr trygge prosjekter, gode betingelser og profesjonelt arbeidsmiljø. Bli med på laget i dag!"
        />
        <meta name="keywords" content="stillasjobb Oslo, stillas montør jobb, bli stillasmontør, LAYHER stillas, HAKI stillas arbeid, stillasarbeid Oslo, stillasjobb Viken, stillas jobb, montør stillas" />
        <link rel="canonical" href="https://allstillas.no/stillasjobb-oslo" />
        
        {/* hreflang for language versions - NO is primary */}
        <link rel="alternate" hreflang="no" href="https://allstillas.no/stillasjobb-oslo" />
        <link rel="alternate" hreflang="en" href="https://allstillas.no/stillasjobb-oslo?lang=en" />
        <link rel="alternate" hreflang="x-default" href="https://allstillas.no/stillasjobb-oslo" />
        
        {/* Open Graph */}
        <meta property="og:title" content="Stillasjobb i Oslo | Bli stillasmontør hos AllStillas" />
        <meta property="og:description" content="Søk jobb som stillasmontør i Oslo. Trygge prosjekter, gode betingelser og profesjonelt arbeidsmiljø." />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://allstillas.no/stillasjobb-oslo" />
        <meta property="og:locale" content="nb_NO" />
        
        {/* Structured Data for Job Posting */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "JobPosting",
            "title": "Stillasmontør",
            "description": "Søk jobb som stillasmontør i Oslo. AllStillas tilbyr trygge prosjekter med LAYHER og HAKI stillas, gode betingelser og profesjonelt arbeidsmiljø.",
            "hiringOrganization": {
              "@type": "Organization",
              "name": "AllStillas",
              "sameAs": "https://allstillas.no"
            },
            "jobLocation": {
              "@type": "Place",
              "address": {
                "@type": "PostalAddress",
                "addressLocality": "Oslo",
                "addressRegion": "Oslo",
                "addressCountry": "NO"
              }
            },
            "employmentType": ["FULL_TIME", "CONTRACTOR"],
            "industry": "Construction",
            "occupationalCategory": "47-2221.00"
          })}
        </script>
        
        {/* FAQ Schema for Rich Results */}
        <script type="application/ld+json">
          {JSON.stringify(faqSchema)}
        </script>
      </Helmet>

      <div className="min-h-screen bg-[#050505]">
        {/* Navigation Bar */}
        <nav className="fixed top-0 left-0 right-0 z-50 bg-[#050505]/90 backdrop-blur-md border-b border-white/10">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex items-center justify-between h-16">
              <Link to="/" className="flex items-center gap-1">
                <div className="px-2 py-1 bg-[#0A0A0A] border border-[#23F7DD]/50 rounded flex items-center justify-center">
                  <span className="text-[#23F7DD] font-bold text-base font-['Unbounded']">All</span>
                </div>
                <span className="text-lg font-bold font-['Unbounded'] text-white">Stillas</span>
              </Link>
              
              <div className="flex items-center gap-2 sm:gap-3">
                <Link to="/">
                  <Button variant="ghost" className="text-white hover:text-[#23F7DD] hidden sm:inline-flex">
                    {t("platformGuide.home")}
                  </Button>
                </Link>
                <LanguageSwitcher variant="full" className="text-white" />
                <Link to="/login">
                  <Button variant="ghost" className="text-white hover:text-[#23F7DD] hidden sm:inline-flex">
                    {t("stillasfirma.login")}
                  </Button>
                </Link>
                <a href="tel:+4790546475">
                  <Button size="sm" className="bg-[#23F7DD] text-[#050505] hover:bg-[#70FFEF] font-bold">
                    <Phone className="w-4 h-4 sm:mr-1" />
                    <span className="hidden sm:inline">{lang === "no" ? "Ring nå 905 46 475" : "Call now 905 46 475"}</span>
                  </Button>
                </a>
              </div>
            </div>
          </div>
        </nav>

        {/* Hero Section */}
        <section className="relative pt-32 pb-20 px-4 overflow-hidden">
          {/* Background gradient */}
          <div className="absolute inset-0 bg-gradient-to-b from-[#23F7DD]/5 via-transparent to-transparent" />
          <div className="absolute top-20 left-1/4 w-96 h-96 bg-[#23F7DD]/10 rounded-full blur-3xl" />
          <div className="absolute top-40 right-1/4 w-64 h-64 bg-[#F7931A]/10 rounded-full blur-3xl" />
          
          <div className="max-w-4xl mx-auto text-center relative z-10">
            {/* Urgency Badges */}
            <div className="flex flex-wrap items-center justify-center gap-3 mb-6">
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-[#23F7DD]/10 border border-[#23F7DD]/30 rounded-full animate-pulse">
                <HardHat className="w-4 h-4 text-[#23F7DD]" />
                <span className="text-sm text-[#23F7DD] font-bold">{t("stillasjobb.badge")}</span>
              </div>
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-[#F7931A]/10 border border-[#F7931A]/30 rounded-full">
                <Zap className="w-4 h-4 text-[#F7931A]" />
                <span className="text-sm text-[#F7931A] font-bold">{t("stillasjobb.urgencyBadge")}</span>
              </div>
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-red-500/10 border border-red-500/30 rounded-full">
                <AlertCircle className="w-4 h-4 text-red-400" />
                <span className="text-sm text-red-400 font-bold">{t("stillasjobb.limitedSpots")}</span>
              </div>
            </div>
            
            {/* H1 - Main SEO Title */}
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-['Unbounded'] font-bold text-white mb-4 leading-tight">
              {t("stillasjobb.heroTitle")}
            </h1>
            <p className="text-xl sm:text-2xl text-[#23F7DD] font-semibold mb-8">
              {t("stillasjobb.heroSubtitle")}
            </p>
            
            {/* Trust Points */}
            <div className="flex flex-wrap items-center justify-center gap-4 sm:gap-6 mb-8">
              <span className="flex items-center gap-2 text-white">
                <CheckCircle2 className="w-5 h-5 text-[#23F7DD]" />
                {t("stillasjobb.heroPoint1")}
              </span>
              <span className="flex items-center gap-2 text-white">
                <CheckCircle2 className="w-5 h-5 text-[#23F7DD]" />
                {t("stillasjobb.heroPoint2")}
              </span>
              <span className="flex items-center gap-2 text-white">
                <CheckCircle2 className="w-5 h-5 text-[#23F7DD]" />
                {t("stillasjobb.heroPoint3")}
              </span>
            </div>
            
            {/* Primary CTA */}
            <div className="flex flex-col items-center gap-4">
              <div className="flex flex-col sm:flex-row items-center gap-3">
                <a href="#soknad">
                  <Button 
                    size="lg" 
                    className="bg-[#23F7DD] text-[#050505] hover:bg-[#70FFEF] font-bold text-lg px-10 py-7 rounded-xl shadow-lg shadow-[#23F7DD]/30 group"
                    data-testid="hero-cta-apply"
                  >
                    <HardHat className="w-5 h-5 mr-2" />
                    {t("stillasjobb.ctaApply")}
                    <ArrowRight className="w-5 h-5 ml-2 group-hover:translate-x-1 transition-transform" />
                  </Button>
                </a>
                <a href="tel:+4790546475">
                  <Button 
                    size="lg"
                    className="bg-[#F7931A] hover:bg-[#E8820F] text-white font-bold text-lg px-8 py-7 rounded-xl shadow-lg shadow-[#F7931A]/30"
                  >
                    <Phone className="w-5 h-5 mr-2" />
                    {lang === "no" ? "Ring nå" : "Call now"} 905 46 475
                  </Button>
                </a>
              </div>
              <p className="text-[#94A3B8] text-sm">
                ⏱️ {t("stillasjobb.applySubtext")}
              </p>
            </div>
          </div>
        </section>

        {/* What We Offer Section */}
        <section className="py-20 px-4 bg-[#0A0A0A]">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-3xl sm:text-4xl font-['Unbounded'] font-bold text-white mb-4">
                🔧 {t("stillasjobb.whatWeOfferTitle")}
              </h2>
              <p className="text-[#94A3B8] max-w-2xl mx-auto">
                {t("stillasjobb.whatWeOfferSubtitle")}
              </p>
            </div>
            
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
              {[
                { icon: <Wrench className="w-6 h-6" />, text: t("stillasjobb.offerItem1") },
                { icon: <Building2 className="w-6 h-6" />, text: t("stillasjobb.offerItem2") },
                { icon: <MapPin className="w-6 h-6" />, text: t("stillasjobb.offerItem3") },
                { icon: <Clock className="w-6 h-6" />, text: t("stillasjobb.offerItem4") },
              ].map((item, i) => (
                <Card key={i} className="bg-white/5 border-white/10 hover:border-[#23F7DD]/30 transition-colors">
                  <CardContent className="p-6 text-center">
                    <div className="w-12 h-12 bg-[#23F7DD]/10 rounded-xl flex items-center justify-center mx-auto mb-4 text-[#23F7DD]">
                      {item.icon}
                    </div>
                    <p className="text-white font-medium">{item.text}</p>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </section>

        {/* Who Is This For Section */}
        <section className="py-20 px-4">
          <div className="max-w-6xl mx-auto">
            <div className="grid lg:grid-cols-2 gap-12 items-center">
              <div>
                <h2 className="text-3xl sm:text-4xl font-['Unbounded'] font-bold text-white mb-6">
                  👷 {t("stillasjobb.whoIsThisFor")}
                </h2>
                <ul className="space-y-4">
                  {[
                    t("stillasjobb.whoItem1"),
                    t("stillasjobb.whoItem2"),
                    t("stillasjobb.whoItem3"),
                    t("stillasjobb.whoItem4")
                  ].map((item, i) => (
                    <li key={i} className="flex items-center gap-3 text-lg text-[#94A3B8]">
                      <CheckCircle2 className="w-5 h-5 text-[#23F7DD] flex-shrink-0" />
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
              
              <div>
                <h2 className="text-3xl sm:text-4xl font-['Unbounded'] font-bold text-white mb-6">
                  🛡️ {t("stillasjobb.whatWeProvide")}
                </h2>
                <ul className="space-y-4">
                  {[
                    t("stillasjobb.provideItem1"),
                    t("stillasjobb.provideItem2"),
                    t("stillasjobb.provideItem3"),
                    t("stillasjobb.provideItem4"),
                    t("stillasjobb.provideItem5")
                  ].map((item, i) => (
                    <li key={i} className="flex items-center gap-3 text-lg text-[#94A3B8]">
                      <Star className="w-5 h-5 text-[#F7931A] flex-shrink-0" />
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </section>

        {/* Work Area Section */}
        <section className="py-20 px-4 bg-[#0A0A0A]">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-3xl sm:text-4xl font-['Unbounded'] font-bold text-white mb-6">
              📍 {t("stillasjobb.workAreaTitle")}
            </h2>
            <p className="text-[#94A3B8] text-lg mb-8">
              {t("stillasjobb.workAreaSubtitle")}
            </p>
            
            <div className="flex flex-wrap justify-center gap-4 mb-8">
              {[t("stillasfirma.workAreaOslo"), t("stillasfirma.workAreaViken"), t("stillasfirma.workAreaNearby")].map((area, i) => (
                <div 
                  key={i}
                  className="px-6 py-3 bg-white/5 border border-white/10 rounded-full text-white font-medium flex items-center gap-2"
                >
                  <MapPin className="w-4 h-4 text-[#23F7DD]" />
                  {area}
                </div>
              ))}
            </div>
            
            {/* SEO Local Paragraph - Important for Google Local */}
            <p className="text-[#64748B] text-sm max-w-2xl mx-auto leading-relaxed">
              {t("stillasjobb.workAreaSeo")}
            </p>
          </div>
        </section>

        {/* Secondary CTA Section */}
        <section className="py-20 px-4">
          <div className="max-w-4xl mx-auto">
            <Card className="bg-gradient-to-br from-[#23F7DD]/10 to-[#23F7DD]/5 border-[#23F7DD]/30">
              <CardContent className="p-8 sm:p-12 text-center">
                <Award className="w-16 h-16 text-[#23F7DD] mx-auto mb-6" />
                <h2 className="text-3xl sm:text-4xl font-['Unbounded'] font-bold text-white mb-4">
                  {t("stillasjobb.readyToJoin")}
                </h2>
                <p className="text-[#94A3B8] text-lg mb-8 max-w-xl mx-auto">
                  {t("stillasjobb.readyToJoinText")}
                </p>
                <a href="#soknad">
                  <Button 
                    size="lg" 
                    className="bg-[#23F7DD] text-[#050505] hover:bg-[#70FFEF] font-bold text-lg px-10 py-6 rounded-xl"
                    data-testid="secondary-cta-apply"
                  >
                    <Briefcase className="w-5 h-5 mr-2" />
                    {t("stillasjobb.applyAsScaffolder")}
                    <ChevronRight className="w-5 h-5 ml-2" />
                  </Button>
                </a>
              </CardContent>
            </Card>
          </div>
        </section>

        {/* Application Form Section */}
        <section id="soknad" className="py-20 px-4 bg-gradient-to-b from-[#0A0A0A] to-[#050505]">
          <div className="max-w-2xl mx-auto">
            <div className="text-center mb-12">
              <Badge className="bg-[#23F7DD]/20 text-[#23F7DD] border-[#23F7DD]/30 mb-4">
                <Send className="w-4 h-4 mr-2" />
                {t("stillasjobb.ctaApply")}
              </Badge>
              <h2 className="text-3xl sm:text-4xl font-['Unbounded'] font-bold text-white mb-4">
                📝 {t("stillasjobb.formTitle")}
              </h2>
              <p className="text-[#94A3B8]">
                {t("stillasjobb.formSubtitle")}
              </p>
            </div>

            {submitted ? (
              <Card className="bg-[#23F7DD]/10 border-[#23F7DD]/30">
                <CardContent className="p-8 text-center">
                  <CheckCircle2 className="w-16 h-16 text-[#23F7DD] mx-auto mb-4" />
                  <h3 className="text-2xl font-bold text-white mb-2">{t("stillasjobb.thankYou")}</h3>
                  <p className="text-[#94A3B8] mb-6">{t("stillasjobb.applicationReceived")}</p>
                  <Button 
                    onClick={() => {
                      setSubmitted(false);
                      setFormData({
                        full_name: "",
                        email: "",
                        phone: "",
                        experience: "",
                        certifications: "",
                        message: "",
                        is_team: false,
                        team_size: 2
                      });
                      setCvFile(null);
                      setCvUrl(null);
                    }}
                    variant="outline"
                    className="border-[#23F7DD] text-[#23F7DD] hover:bg-[#23F7DD]/10"
                  >
                    {t("stillasjobb.sendNewApplication")}
                  </Button>
                </CardContent>
              </Card>
            ) : (
              <Card className="bg-white/5 border-white/10">
                <CardContent className="p-6 sm:p-8">
                  <form onSubmit={handleSubmit} className="space-y-6">
                    {/* Individual or Team */}
                    <div className="flex gap-4 mb-6">
                      <button
                        type="button"
                        onClick={() => setFormData({...formData, is_team: false})}
                        className={`flex-1 p-4 rounded-xl border transition-all ${
                          !formData.is_team 
                            ? "bg-[#23F7DD]/20 border-[#23F7DD] text-white" 
                            : "bg-white/5 border-white/10 text-[#94A3B8] hover:border-white/20"
                        }`}
                      >
                        <User className="w-6 h-6 mx-auto mb-2" />
                        <p className="font-medium">{t("stillasjobb.individual")}</p>
                      </button>
                      <button
                        type="button"
                        onClick={() => setFormData({...formData, is_team: true})}
                        className={`flex-1 p-4 rounded-xl border transition-all ${
                          formData.is_team 
                            ? "bg-[#23F7DD]/20 border-[#23F7DD] text-white" 
                            : "bg-white/5 border-white/10 text-[#94A3B8] hover:border-white/20"
                        }`}
                      >
                        <Users className="w-6 h-6 mx-auto mb-2" />
                        <p className="font-medium">{t("stillasjobb.teamOption")}</p>
                      </button>
                    </div>

                    {formData.is_team && (
                      <div>
                        <Label className="text-white">{t("stillasjobb.teamSize")}</Label>
                        <Input
                          type="number"
                          min="2"
                          max="20"
                          value={formData.team_size}
                          onChange={(e) => setFormData({...formData, team_size: parseInt(e.target.value) || 2})}
                          className="bg-white/5 border-white/10 text-white mt-1"
                        />
                      </div>
                    )}

                    {/* Contact Info */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div>
                        <Label className="text-white">{t("stillasjobb.fullName")} *</Label>
                        <Input
                          required
                          placeholder={t("stillasjobb.namePlaceholder")}
                          value={formData.full_name}
                          onChange={(e) => setFormData({...formData, full_name: e.target.value})}
                          className="bg-white/5 border-white/10 text-white mt-1"
                        />
                      </div>
                      <div>
                        <Label className="text-white">{t("stillasjobb.phone")} *</Label>
                        <Input
                          required
                          type="tel"
                          placeholder={t("stillasjobb.phonePlaceholder")}
                          value={formData.phone}
                          onChange={(e) => setFormData({...formData, phone: e.target.value})}
                          className="bg-white/5 border-white/10 text-white mt-1"
                        />
                      </div>
                    </div>

                    <div>
                      <Label className="text-white">{t("stillasjobb.email")} *</Label>
                      <Input
                        required
                        type="email"
                        placeholder={t("stillasjobb.emailPlaceholder")}
                        value={formData.email}
                        onChange={(e) => setFormData({...formData, email: e.target.value})}
                        className="bg-white/5 border-white/10 text-white mt-1"
                      />
                    </div>

                    {/* Experience */}
                    <div>
                      <Label className="text-white">{t("stillasjobb.experience")}</Label>
                      <Textarea
                        placeholder={t("stillasjobb.experiencePlaceholder")}
                        value={formData.experience}
                        onChange={(e) => setFormData({...formData, experience: e.target.value})}
                        className="bg-white/5 border-white/10 text-white mt-1 min-h-[80px]"
                      />
                    </div>

                    <div>
                      <Label className="text-white">{t("stillasjobb.certifications")}</Label>
                      <Input
                        placeholder={t("stillasjobb.certificationsPlaceholder")}
                        value={formData.certifications}
                        onChange={(e) => setFormData({...formData, certifications: e.target.value})}
                        className="bg-white/5 border-white/10 text-white mt-1"
                      />
                    </div>

                    {/* CV Upload - OPTIONAL */}
                    <div>
                      <Label className="text-white flex items-center gap-2">
                        {t("stillasjobb.uploadCV")}
                        <span className="text-xs text-[#94A3B8] font-normal bg-white/10 px-2 py-0.5 rounded">
                          {lang === "no" ? "Ikke obligatorisk" : "Not required"}
                        </span>
                      </Label>
                      <div className="mt-1">
                        <label className="flex items-center justify-center gap-3 p-4 border-2 border-dashed border-white/20 rounded-xl cursor-pointer hover:border-[#23F7DD]/50 transition-colors">
                          <input
                            type="file"
                            accept=".pdf,.doc,.docx,image/*,.jpg,.jpeg,.png,.webp"
                            onChange={handleCvUpload}
                            className="hidden"
                          />
                          {uploading ? (
                            <>
                              <Loader2 className="w-5 h-5 text-[#23F7DD] animate-spin" />
                              <span className="text-[#94A3B8]">{t("stillasjobb.sending")}</span>
                            </>
                          ) : cvFile ? (
                            <>
                              {cvFile.type.startsWith('image/') ? (
                                <ImageIcon className="w-5 h-5 text-[#23F7DD]" />
                              ) : (
                                <FileText className="w-5 h-5 text-[#23F7DD]" />
                              )}
                              <span className="text-white">{cvFile.name}</span>
                              <CheckCircle2 className="w-5 h-5 text-green-400" />
                            </>
                          ) : (
                            <>
                              <Upload className="w-5 h-5 text-[#94A3B8]" />
                              <span className="text-[#94A3B8]">
                                {lang === "no" 
                                  ? "Klikk for å laste opp CV eller bilde (PDF, DOC, JPG, PNG)" 
                                  : "Click to upload CV or image (PDF, DOC, JPG, PNG)"}
                              </span>
                            </>
                          )}
                        </label>
                        <p className="text-xs text-[#64748B] mt-2 text-center">
                          {lang === "no" 
                            ? "💡 Har du ikke CV? Ingen problem! Beskriv din erfaring i feltene under." 
                            : "💡 Don't have a CV? No problem! Describe your experience in the fields below."}
                        </p>
                      </div>
                    </div>

                    {/* Additional Message - More prominent */}
                    <div>
                      <Label className="text-white flex items-center gap-2">
                        {lang === "no" ? "Fortell oss om deg selv" : "Tell us about yourself"}
                        <span className="text-xs text-[#23F7DD] font-normal">
                          {lang === "no" ? "✨ Viktig hvis du ikke har CV" : "✨ Important if you don't have CV"}
                        </span>
                      </Label>
                      <Textarea
                        placeholder={lang === "no" 
                          ? "Beskriv din bakgrunn, erfaring, motivasjon... Alt som kan hjelpe oss å bli kjent med deg!" 
                          : "Describe your background, experience, motivation... Anything that helps us get to know you!"}
                        value={formData.message}
                        onChange={(e) => setFormData({...formData, message: e.target.value})}
                        className="bg-white/5 border-white/10 text-white mt-1 min-h-[120px]"
                      />
                    </div>

                    {/* Submit Button */}
                    <Button
                      type="submit"
                      disabled={submitting}
                      className="w-full bg-[#23F7DD] text-[#050505] hover:bg-[#70FFEF] font-bold text-lg py-6"
                    >
                      {submitting ? (
                        <>
                          <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                          {t("stillasjobb.sending")}
                        </>
                      ) : (
                        <>
                          <Send className="w-5 h-5 mr-2" />
                          {t("stillasjobb.sendApplication")}
                        </>
                      )}
                    </Button>

                    <p className="text-xs text-[#94A3B8] text-center">
                      {t("stillasjobb.privacyNote")}
                    </p>
                  </form>
                </CardContent>
              </Card>
            )}
          </div>
        </section>

        {/* FAQ Section */}
        <section className="py-20 px-4 bg-[#0A0A0A]">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl sm:text-4xl font-['Unbounded'] font-bold text-white mb-12 text-center">
              ❓ {t("stillasfirma.faqTitle")}
            </h2>
            
            <div className="space-y-6">
              {faqData.map((faq, i) => (
                <Card key={i} className="bg-white/5 border-white/10">
                  <CardContent className="p-6">
                    <h3 className="text-lg font-bold text-white mb-2 flex items-start gap-2">
                      <span className="text-[#23F7DD]">Q:</span>
                      {faq.q}
                    </h3>
                    <p className="text-[#94A3B8] pl-6">
                      <span className="text-[#F7931A] font-bold">A:</span> {faq.a}
                    </p>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </section>

        {/* Contact Section */}
        <section className="py-20 px-4">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-3xl sm:text-4xl font-['Unbounded'] font-bold text-white mb-6">
              📞 {t("stillasjobb.contactTitle")}
            </h2>
            <p className="text-[#94A3B8] text-lg mb-8">
              {t("stillasjobb.contactText")}
            </p>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-3xl mx-auto" data-testid="contact-team">
              {/* Henning - Site Leader */}
              <div className="bg-white/5 border border-white/10 rounded-2xl p-6 hover:border-[#23F7DD]/40 transition-colors">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-12 h-12 rounded-full bg-gradient-to-br from-[#23F7DD] to-[#1de0c9] flex items-center justify-center font-bold text-black text-lg">
                    H
                  </div>
                  <div className="text-left">
                    <p className="text-white font-bold">Henning</p>
                    <p className="text-[#23F7DD] text-sm">Stire Leader</p>
                  </div>
                </div>
                <div className="space-y-2">
                  <a
                    href="tel:+4790546475"
                    className="flex items-center gap-3 px-4 py-3 bg-white/5 border border-white/10 rounded-xl hover:border-[#23F7DD]/40 transition-colors"
                    data-testid="henning-phone"
                  >
                    <Phone className="w-4 h-4 text-[#23F7DD] flex-shrink-0" />
                    <span className="text-white text-sm">+47 905 46 475</span>
                  </a>
                  <a
                    href="mailto:post@doserne.com"
                    className="flex items-center gap-3 px-4 py-3 bg-white/5 border border-white/10 rounded-xl hover:border-[#23F7DD]/40 transition-colors"
                    data-testid="henning-email"
                  >
                    <Mail className="w-4 h-4 text-[#23F7DD] flex-shrink-0" />
                    <span className="text-white text-sm">post@doserne.com</span>
                  </a>
                </div>
              </div>

              {/* Bogdan - Project Leader */}
              <div className="bg-white/5 border border-white/10 rounded-2xl p-6 hover:border-[#F7931A]/40 transition-colors">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-12 h-12 rounded-full bg-gradient-to-br from-[#F7931A] to-[#FFCD00] flex items-center justify-center font-bold text-black text-lg">
                    B
                  </div>
                  <div className="text-left">
                    <p className="text-white font-bold">Bogdan</p>
                    <p className="text-[#F7931A] text-sm">{lang === "no" ? "Project Leader" : "Project Leader"}</p>
                  </div>
                </div>
                <div className="space-y-2">
                  <a
                    href="tel:+4793922819"
                    className="flex items-center gap-3 px-4 py-3 bg-white/5 border border-white/10 rounded-xl hover:border-[#F7931A]/40 transition-colors"
                    data-testid="bogdan-phone"
                  >
                    <Phone className="w-4 h-4 text-[#F7931A] flex-shrink-0" />
                    <span className="text-white text-sm">+47 9392 2819</span>
                  </a>
                  <a
                    href="mailto:bogdan@doserne.com"
                    className="flex items-center gap-3 px-4 py-3 bg-white/5 border border-white/10 rounded-xl hover:border-[#F7931A]/40 transition-colors"
                    data-testid="bogdan-email"
                  >
                    <Mail className="w-4 h-4 text-[#F7931A] flex-shrink-0" />
                    <span className="text-white text-sm">bogdan@doserne.com</span>
                  </a>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Final CTA */}
        <section className="py-16 px-4 bg-gradient-to-t from-[#23F7DD]/10 to-transparent">
          <div className="max-w-4xl mx-auto text-center">
            <p className="text-2xl text-white font-bold mb-6">
              {t("stillasjobb.takeTheStep")}
            </p>
            <a href="#soknad">
              <Button 
                size="lg" 
                className="bg-[#23F7DD] text-[#050505] hover:bg-[#70FFEF] font-bold text-lg px-10 py-6 rounded-xl"
                data-testid="final-cta-apply"
              >
                <HardHat className="w-5 h-5 mr-2" />
                {t("stillasjobb.sendApplicationNow")}
                <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
            </a>
          </div>
        </section>

        {/* Footer */}
        <footer className="py-8 px-4 border-t border-white/10">
          <div className="max-w-6xl mx-auto">
            {/* Contact Info Bar - Henning + Bogdan */}
            <div className="flex flex-wrap items-center justify-center gap-x-6 gap-y-3 mb-6 p-4 bg-white/5 rounded-lg border border-white/10">
              <a href="tel:+4790546475" className="flex items-center gap-2 text-white hover:text-[#23F7DD] transition-colors">
                <Phone className="w-5 h-5 text-[#23F7DD]" />
                <span className="font-semibold">Henning: 905 46 475</span>
              </a>
              <a href="mailto:post@doserne.com" className="flex items-center gap-2 text-white hover:text-[#23F7DD] transition-colors">
                <Mail className="w-5 h-5 text-[#23F7DD]" />
                <span>post@doserne.com</span>
              </a>
              <span className="text-white/20">|</span>
              <a href="tel:+4793922819" className="flex items-center gap-2 text-white hover:text-[#F7931A] transition-colors">
                <Phone className="w-5 h-5 text-[#F7931A]" />
                <span className="font-semibold">Bogdan: 9392 2819</span>
              </a>
              <a href="mailto:bogdan@doserne.com" className="flex items-center gap-2 text-white hover:text-[#F7931A] transition-colors">
                <Mail className="w-5 h-5 text-[#F7931A]" />
                <span>bogdan@doserne.com</span>
              </a>
              <span className="flex items-center gap-2 text-[#94A3B8]">
                <MapPin className="w-5 h-5 text-[#23F7DD]" />
                <span>Oslo & Viken</span>
              </span>
            </div>
            
            <div className="flex flex-col sm:flex-row items-center justify-between gap-4 mb-6">
              <div className="flex items-center gap-2">
                <Link to="/" className="flex items-center gap-1">
                  <div className="px-2 py-1 bg-[#0A0A0A] border border-[#23F7DD]/50 rounded">
                    <span className="text-[#23F7DD] font-bold text-sm font-['Unbounded']">All</span>
                  </div>
                  <span className="text-sm font-bold font-['Unbounded'] text-white">Stillas</span>
                </Link>
                <div className="inline-flex items-center gap-1 px-2 py-0.5 bg-[#23F7DD]/10 border border-[#23F7DD]/20 rounded-full">
                  <span className="text-[#23F7DD] text-[10px]">⚡</span>
                  <span className="text-[10px] text-[#94A3B8]">Powered by</span>
                  <span className="text-[10px] text-[#23F7DD]">Doserne AS</span>
                </div>
              </div>
              
              <p className="text-sm text-[#64748B]">© 2026 Doserne AS. All rights reserved.</p>
              
              <div className="flex items-center gap-4 text-sm text-[#64748B]">
                <Link to="/privacy" className="hover:text-white transition-colors">{t("stillasjobb.privacy")}</Link>
                <Link to="/terms" className="hover:text-white transition-colors">{t("stillasfirma.career")}</Link>
              </div>
            </div>
            
            {/* SEO Boost Text - Small, subtle, helps Google */}
            <p className="text-xs text-[#475569] text-center">
              {t("stillasjobb.seoText")}
            </p>
          </div>
        </footer>
      </div>
    </>
  );
}
